import React from 'react'
import { CurrencyFormat, DateFormat, PercentFormat } from '../../../../helpers/formatting'
import ManagerApprovalIcons from '../../../../components/elements/ManagerApprovalIcons'

/**
 * Step 4 of the payment-plan wizard — read-only "Existing Payment Plan" view
 * shown when the loan already has a payment plan (collections agent revisits an
 * existing plan to resend agreement, etc.). The schedule rows render from
 * `responseData.payment_plan_payments`; the action row is only shown when the
 * agent has the right permissions (controlled by the parent via
 * `displayActionInStep4`).
 *
 * Behaviour-preserving extraction from the god-component PaymentPlanPayment.jsx.
 * Parent passes everything as props; no internal state.
 */
export default function PaymentPlanStep4({
  responseData,
  dropdownOptions,
  saving,
  displayActionInStep4,
  setStep,
  checkChangesMade,
  handleSubmit,
  onSubmit,
}) {
  const renderExistingPaymentPlanPayments = () =>
    responseData?.payment_plan_payments?.map((plan, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <tr key={index}>
        <td><DateFormat dateString={plan.payment_date} /></td>
        <td>{dropdownOptions.filter((o) => o.id === plan?.payment_method_dropdown_option_id)[0].name}</td>
        <td>{dropdownOptions.filter((o) => o.code_key === 'loan_status_collections')[0].name}</td>
        <td><CurrencyFormat value={plan.principal_amount} showCurrencySign={false} /></td>
        <td><CurrencyFormat value={plan.interest_amount} showCurrencySign={false} /></td>
        <td><CurrencyFormat value={plan.late_fee} showCurrencySign={false} /></td>
        <td><CurrencyFormat value={plan.nsf_fee} showCurrencySign={false} /></td>
        <td><CurrencyFormat value={plan.total_amount} showCurrencySign={false} /></td>
        <td>{dropdownOptions.filter((o) => o.code_key === 'payment_status_scheduled')[0].name}</td>
      </tr>
    ))

  return (
    <form onSubmit={handleSubmit(onSubmit)} id="payment_plan_payment_step3_form">
      <div className="title is-5" data-testid="step4-heading">Existing Payment Plan {
        responseData?.mgr_approval_status_dropdown_option_id && (
          <ManagerApprovalIcons type={`${dropdownOptions.find((x) => x?.id === responseData?.mgr_approval_status_dropdown_option_id)?.code_key}`} />
        )
      }
      </div>
      <table className="table is-narrow table-payment-schedule-panel">
        <tbody>
          <tr data-testid="step4-balance-row-outstanding">
            <td><b>Outstanding Balance</b></td>
            <td><CurrencyFormat value={responseData?.outstanding_total} /></td>
            <td><b>Discount</b></td>
            <td><PercentFormat value={responseData?.discount_percent} /></td>
            <td></td>
            <td></td>
          </tr>

          <tr data-testid="step4-balance-row-settlement">
            <td><b>Settlement Balance</b></td>
            <td><CurrencyFormat value={responseData?.owed_amount} /></td>
            <td><b>Discount</b></td>
            <td><CurrencyFormat value={responseData?.discount_amount} /></td>
            <td></td>
            <td></td>
          </tr>
        </tbody>
      </table>

      <table className="table is-striped is-fullwidth" data-testid="step4-schedule-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Method</th>
            <th>Type</th>
            <th>Principal</th>
            <th>Interest</th>
            <th>Late Fee</th>
            <th>NSF Fee</th>
            <th>Total</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {renderExistingPaymentPlanPayments()}
        </tbody>
      </table>

      {displayActionInStep4 && (
        <table className="table is-narrow table-payment-schedule-panel">
          <tbody>
            <tr>
              <td></td>
              <td colSpan={2}>
                <div className="has-text-right">
                  <button
                    type="button"
                    data-testid="step4-cancel"
                    className="button has-background-grey has-text-white is-small mr-1"
                    onClick={() => checkChangesMade()}
                  >
                    <span className="mx-3">Cancel</span>
                  </button>
                  <button
                    type="button"
                    data-testid="step4-back"
                    className="button has-background-grey has-text-white is-small mr-1"
                    onClick={() => setStep("step1")}
                  >
                    <span className="mx-3">Back</span>
                  </button>
                  <button
                    type="submit"
                    data-testid="step4-send"
                    className="button is-link is-small mr-1"
                    form="payment_plan_payment_step3_form"
                    disabled={saving && 'disabled'}
                  >
                    {responseData?.is_started == true ? (
                      <span className="mx-3">{saving ? 'Resending Agreement' : 'Resend Agreement'}</span>
                    ) : (
                      <span className="mx-3">{saving ? 'Sending' : 'Send Confirmation'}</span>
                    )}
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      )}
      {displayActionInStep4 === false && (
        <table className="table is-narrow table-payment-schedule-panel">
          <tbody>
            <tr>
              <td></td>
              <td colSpan={2}>
                <div className="has-text-right">
                  <button
                    type="button"
                    data-testid="step4-readonly-cancel"
                    className="button has-background-grey has-text-white is-small mr-1"
                    onClick={() => checkChangesMade('form_submit')}
                  >
                    <span className="mx-3">Cancel</span>
                  </button>
                  <button
                    type="button"
                    data-testid="step4-readonly-back"
                    className="button has-background-grey has-text-white is-small mr-1"
                    onClick={() => setStep("step1")}
                  >
                    <span className="mx-3">Back</span>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      )}
    </form>
  )
}
